import { useRef, useState, useCallback, useEffect } from 'react';
import { Auth } from 'aws-amplify';

// WebSocket connection states
export const WS_STATES = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  ERROR: 'error',
  RECONNECTING: 'reconnecting'
};

// Message types for better organization
export const MESSAGE_TYPES = {
  // Outgoing message types (Frontend -> Backend)
  LIVE_AUDIO_STREAM: 'live_audio_stream',
  AUDIO_CHUNK_LIVE: 'audio_chunk_live', // Legacy support
  START_INTERVIEW: 'start_interview', // Includes: interviewType ('screening' = 20 min, 'jd_specific' = 30 min)
  SCREEN_RECORDING_COMPLETE: 'screen_recording_complete',
  CONCLUSION_PLAYED: 'conclusion_played',
  PONG: 'pong',
  
  // Media Server message types (Frontend -> Media Server)
  MEDIA_SERVER_RECORDING_COMPLETE: 'media_server_recording_complete',
  
  // Incoming message types (Backend -> Frontend)
  SYNTHESIZED_SPEECH: 'synthesized_speech',
  STREAMING_INTERIM_TRANSCRIPT: 'streaming_interim_transcript',
  FINAL_CONSOLIDATED_TRANSCRIPT: 'final_consolidated_transcript', // Backend signal: Speech ended, close mic
  ENHANCED_STT_ERROR: 'enhanced_stt_error',
  LIVE_CAPTION: 'live_caption', // Legacy support
  TRANSCRIPT_FINAL: 'transcript_final', // Legacy support
  PING: 'ping',
  CONCLUSION_MESSAGE: 'conclusion_message',
  
  // Media Server message types (Media Server -> Frontend via Backend)
  MEDIA_SERVER_RECORDING_URL: 'media_server_recording_url',
  INTERVIEW_COMPLETED: 'interview_completed',
  VIDEO_URL_RECEIVED: 'video_url_received',
  EVALUATION_COMPLETE: 'evaluation_complete'
};

/**
 * Custom hook for managing WebSocket connection and message handling
 * @param {Object} config - Configuration object
 * @param {string} config.url - WebSocket URL
 * @param {function} config.onMessage - Message handler function
 * @param {boolean} config.autoConnect - Whether to auto-connect on mount
 * @param {number} config.reconnectAttempts - Max reconnection attempts (default 10)
 * @param {number} config.reconnectDelay - Base delay before exponential backoff (default 1000)
 * @param {number} config.maxReconnectDelay - Cap on backoff delay (default 15000)
 * @param {number} config.keepaliveIntervalMs - Frontend ping interval (default 25000, 0 disables)
 * @param {number} config.silentDeathThresholdMs - Force-reconnect if no inbound traffic for this long (default 40000, 0 disables)
 */
export const useWebSocket = ({
  url = 'wss://phone.getscreened.in/getscreenedbot/ws',
  onMessage,
  onReconnect,
  onDisconnect,
  autoConnect = false,
  reconnectAttempts = 10,
  reconnectDelay = 1000,
  maxReconnectDelay = 15000,
  keepaliveIntervalMs = 25000,
  silentDeathThresholdMs = 40000
}) => {
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectCountRef = useRef(0);
  // Lifetime-OPEN counter. 0 means we've never had a successful open yet —
  // the next open is the initial connect. 1+ means subsequent reopens are
  // reconnects, which is when resume_session should fire. Cleared on
  // disconnect() so manual stop+restart correctly starts fresh.
  const totalOpensRef = useRef(0);
  const messageQueueRef = useRef([]);
  // Liveness tracking. lastInboundAt is updated by every incoming message
  // (including server pings). If it goes stale beyond silentDeathThresholdMs
  // while the socket reports OPEN, the connection has silently died (NAT
  // timeout, network roaming, server crash without RST) and we force-close
  // it to trigger the standard reconnect path. Without this, a dead socket
  // can sit half-open for minutes — long enough to break a 20-min interview.
  const lastInboundAtRef = useRef(0);
  const keepaliveTimerRef = useRef(null);

  const [connectionState, setConnectionState] = useState(WS_STATES.DISCONNECTED);
  const [lastError, setLastError] = useState(null);

  // Clear reconnection timeout
  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const clearKeepaliveTimer = useCallback(() => {
    if (keepaliveTimerRef.current) {
      clearInterval(keepaliveTimerRef.current);
      keepaliveTimerRef.current = null;
    }
  }, []);

  // Compute backoff delay with exponential growth + jitter, capped.
  // Earlier code used reconnectDelay * count (linear) and was labelled
  // "exponential" in a comment — it was neither. With max 3 attempts that
  // meant 1s → 2s → 3s and total give-up at ~6s, far too short for a 20-min
  // interview on a transient network blip.
  const computeBackoff = useCallback((attempt) => {
    const exp = reconnectDelay * Math.pow(2, Math.max(0, attempt - 1));
    const capped = Math.min(exp, maxReconnectDelay);
    const jitter = Math.random() * Math.min(1000, capped * 0.25);
    return Math.floor(capped + jitter);
  }, [reconnectDelay, maxReconnectDelay]);

  // Send queued messages when connection is established
  const sendQueuedMessages = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN && messageQueueRef.current.length > 0) {
      console.log(`📤 Sending ${messageQueueRef.current.length} queued messages`);
      messageQueueRef.current.forEach(message => {
        try {
          wsRef.current.send(message);
        } catch (error) {
          console.error('❌ Failed to send queued message:', error);
        }
      });
      messageQueueRef.current = [];
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Don't connect if already connected or connecting
    if (wsRef.current?.readyState === WebSocket.OPEN || 
        wsRef.current?.readyState === WebSocket.CONNECTING) {
      console.log('🔗 WebSocket already connected or connecting');
      return;
    }

    console.log(`🔗 Connecting to WebSocket: ${url}`);
    setConnectionState(WS_STATES.CONNECTING);
    setLastError(null);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = async () => {
        console.log('✅ WebSocket connected successfully');
        setConnectionState(WS_STATES.CONNECTED);
        // Snapshot whether this is a reconnect BEFORE clearing the attempt
        // counter so the onReconnect callback below can fire with the
        // attempt number that actually got us here.
        const isReconnectOpen = totalOpensRef.current > 0;
        const reconnectAttemptNum = reconnectCountRef.current;
        totalOpensRef.current += 1;
        reconnectCountRef.current = 0;
        lastInboundAtRef.current = Date.now();
        clearReconnectTimeout();

        // Send ID token for authentication on port 8080 connections
        if (url.includes(':8080')) {
          try {
            console.log('🔐 Port 8080 detected - sending ID token for authentication');
            const session = await Auth.currentSession();
            const idToken = session.getIdToken().getJwtToken(); // ✅ Correct ID token

            const authMessage = {
              type: 'auth',
              token: idToken // This is an ID token with 'aud' claim
            };

            console.log('📤 Sending ID token authentication to WebSocket server');
            ws.send(JSON.stringify(authMessage));
          } catch (authError) {
            console.error('❌ Failed to authenticate with WebSocket server:', authError);
            setLastError(authError);
            // Don't close connection, let it continue - some servers might not require auth
          }
        }

        // Send any queued messages
        sendQueuedMessages();

        // After a reconnect, let the parent send a resume_session frame
        // so the bot reattaches the preserved bot_manager for this slot.
        // Fired here (not in onclose) because we need an OPEN socket to
        // actually send the resume payload.
        if (isReconnectOpen && typeof onReconnect === 'function') {
          try {
            onReconnect({ reconnectAttempt: reconnectAttemptNum, send: (msg) => {
              try {
                ws.send(typeof msg === 'string' ? msg : JSON.stringify(msg));
                return true;
              } catch (err) {
                console.error('❌ Failed to send resume payload:', err);
                return false;
              }
            }});
          } catch (cbErr) {
            console.error('❌ onReconnect callback threw:', cbErr);
          }
        }

        // Start keepalive. Frontend sends a ping every keepaliveIntervalMs;
        // the existing handlePing on the backend reciprocates with a pong,
        // which lands as ws.onmessage and refreshes lastInboundAtRef. If we
        // see no inbound for silentDeathThresholdMs, force-close so the
        // standard onclose → reconnect path fires.
        clearKeepaliveTimer();
        if (keepaliveIntervalMs > 0) {
          keepaliveTimerRef.current = setInterval(() => {
            const sock = wsRef.current;
            if (!sock || sock.readyState !== WebSocket.OPEN) return;

            if (silentDeathThresholdMs > 0) {
              const sinceInbound = Date.now() - lastInboundAtRef.current;
              if (sinceInbound > silentDeathThresholdMs) {
                console.warn(`⚠️ WebSocket silently dead (${sinceInbound}ms since last inbound) — force-closing to trigger reconnect`);
                try {
                  sock.close(4000, 'silent death detected');
                } catch (err) {
                  console.error('❌ Force-close failed:', err);
                }
                return;
              }
            }

            try {
              sock.send(JSON.stringify({ type: 'ping', timestamp: Date.now() }));
            } catch (err) {
              console.error('❌ Keepalive ping send failed:', err);
            }
          }, keepaliveIntervalMs);
        }
      };

      ws.onmessage = (event) => {
        lastInboundAtRef.current = Date.now();
        try {
          const data = JSON.parse(event.data);
          onMessage?.(data, event);
        } catch (error) {
          console.error('❌ Failed to parse WebSocket message:', error);
          console.log('Raw message:', event.data);
        }
      };

      ws.onclose = (event) => {
        console.log('🔌 WebSocket connection closed:', event.code, event.reason);
        wsRef.current = null;
        clearKeepaliveTimer();

        if (typeof onDisconnect === 'function') {
          try {
            onDisconnect({ code: event.code, reason: event.reason, wasClean: event.wasClean });
          } catch (cbErr) {
            console.error('❌ onDisconnect callback threw:', cbErr);
          }
        }

        if (event.code !== 1000 && reconnectCountRef.current < reconnectAttempts) {
          // Abnormal closure, attempt to reconnect
          setConnectionState(WS_STATES.RECONNECTING);
          reconnectCountRef.current++;

          const delay = computeBackoff(reconnectCountRef.current);
          console.log(`🔄 Attempting to reconnect (${reconnectCountRef.current}/${reconnectAttempts}) in ${delay}ms`);

          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          setConnectionState(WS_STATES.DISCONNECTED);
          if (reconnectCountRef.current >= reconnectAttempts) {
            setLastError(new Error(`Failed to reconnect after ${reconnectAttempts} attempts`));
          }
        }
      };

      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
        setLastError(error);
        setConnectionState(WS_STATES.ERROR);
      };

    } catch (error) {
      console.error('❌ Failed to create WebSocket connection:', error);
      setLastError(error);
      setConnectionState(WS_STATES.ERROR);
    }
  }, [url, onMessage, onReconnect, onDisconnect, reconnectAttempts, sendQueuedMessages, clearReconnectTimeout, clearKeepaliveTimer, computeBackoff, keepaliveIntervalMs, silentDeathThresholdMs]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    console.log('🔌 Disconnecting WebSocket');
    clearReconnectTimeout();
    clearKeepaliveTimer();

    if (wsRef.current) {
      // Close with normal closure code
      wsRef.current.close(1000, 'User initiated disconnect');
      wsRef.current = null;
    }

    setConnectionState(WS_STATES.DISCONNECTED);
    reconnectCountRef.current = 0;
    // Reset on manual disconnect so the next connect() is treated as a
    // fresh start — not a resume — preventing stale resume_session frames
    // when a user manually restarts an interview.
    totalOpensRef.current = 0;
    messageQueueRef.current = [];
  }, [clearReconnectTimeout, clearKeepaliveTimer]);

  // Send message with automatic queuing if not connected
  const sendMessage = useCallback((message, queueIfDisconnected = true) => {
    const messageStr = typeof message === 'string' ? message : JSON.stringify(message);
    
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(messageStr);
        return true;
      } catch (error) {
        console.error('❌ Failed to send WebSocket message:', error);
        return false;
      }
    } else if (queueIfDisconnected) {
      console.log('📝 Queuing message for when WebSocket connects');
      messageQueueRef.current.push(messageStr);
      return false; // Message queued, not sent immediately
    } else {
      console.warn('⚠️ WebSocket not connected and queuing disabled');
      return false;
    }
  }, []);

  // Send message with retry logic
  const sendMessageWithRetry = useCallback(async (message, maxRetries = 3, retryDelay = 1000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      const success = sendMessage(message, false);
      
      if (success) {
        return true;
      }
      
      if (attempt < maxRetries) {
        console.log(`🔄 Retry ${attempt}/${maxRetries} in ${retryDelay}ms`);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
      }
    }
    
    console.error(`❌ Failed to send message after ${maxRetries} attempts`);
    return false;
  }, [sendMessage]);

  // Get current connection status
  const isConnected = connectionState === WS_STATES.CONNECTED;
  const isConnecting = connectionState === WS_STATES.CONNECTING;
  const isReconnecting = connectionState === WS_STATES.RECONNECTING;

  // Auto-connect on mount if enabled
  useEffect(() => {
    if (autoConnect) {
      connect();
    }

    // Cleanup on unmount
    return () => {
      disconnect();
    };
  }, [autoConnect, connect, disconnect]);

  return {
    // Connection management
    connect,
    disconnect,
    
    // Message sending
    sendMessage,
    sendMessageWithRetry,
    
    // Connection state
    connectionState,
    isConnected,
    isConnecting,
    isReconnecting,
    lastError,
    
    // WebSocket reference (for direct access if needed)
    wsRef: wsRef.current,
    
    // Queue status
    queuedMessageCount: messageQueueRef.current.length
  };
};

export default useWebSocket;
